import axios from '../../axios'
import Axios, { AxiosRequestConfig } from 'axios'
import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { toast } from 'react-toastify'
import { Predictor, TemporaryFile } from './types'
import { downloadCSV, isPredictorInProgress } from 'app/helpers'
import { Progress } from '@aws-sdk/lib-storage'
import FileUploader from 'app/services/FileUploader'

export class PredictorsStore {
  loadingList: boolean = false
  list: Predictor[] = []
  entries: {
    [key: string]: Predictor
  } = {}
  totalPages: number = 0
  totalElements: number = 0
  cancelTokenSource = () => {}

  constructor() {
    makeObservable(this, {
      loadingList: observable,
      list: observable,
      entries: observable,
      totalPages: observable,
      totalElements: observable,
      getData: action,
      delete: action,
      trainPredictor: action,
      setPredictor: action,
      reset: action,
      clearList: action,
      removeFromList: action,
      setLoadingList: action,
      inProgressPredictors: computed,
    })
  }

  get inProgressPredictors() {
    return this.list.filter(isPredictorInProgress)
  }

  reset = () => {
    this.clearList()
    this.entries = {}
    this.cancelTokenSource = () => {}
  }

  clearList = () => {
    this.loadingList = false
    this.list = []
    this.totalPages = 0
    this.totalElements = 0
  }

  setLoadingList = (value: boolean) => {
    this.loadingList = value
  }

  removeFromList = (id: number) => {
    this.list = this.list.filter((item) => item.id !== id)
  }

  create = async (data: object, atuotrain: boolean) => {
    return await axios.post(atuotrain ? '/api/v1/predictor/create_train' : '/api/v1/predictor', data).then((response) => {
      toast.success('Created successfully')
      return response
    })
  }

  update = async (id: number, data: object) => {
    return await axios.put('/api/v1/predictor/' + id, data).then((response) => {
      toast.success('Updated successfully')
      return response
    })
  }

  setShareDetails = async (predictorIds: number[], shareDetails: boolean) => {
    let endPoint = shareDetails ? 'enable' : 'disable'
    return await axios
      .put('/api/v1/predictor/share-details/' + endPoint, { predictorIds })
      .then(({ data }) => {
        this.setPredictor(data)
      })
  }

  delete = async (id: number) => {
    await axios.delete('/api/v1/predictor/' + id).then(({ data }) => {
      toast.success(
        'Delete is started. You will be notified when the process is completed'
      )
      this.setPredictor(data)
    })
  }

  getById = async (id: number) => {
    return await axios('/api/v1/predictor/' + id).then(({ data }) => {
      this.setPredictor(data)
      return data
    })
  }

  getData = (filters: any = {}) => {
    if (typeof this.cancelTokenSource == 'function') {
      // this.cancelTokenSource()
    }

    if (filters.withIdsFilter && !filters.ids) {
      this.clearList()
      return
    }

    const cancelTokenSource = Axios.CancelToken.source()

    const params: any = {
      ...filters,
      name: filters.searchValue,
      access_filter: filters.sharingStatus,
    }
    delete params.searchValue
    delete params.ownership
    delete params.sharingStatus

    this.setLoadingList(true)

    const config: AxiosRequestConfig = {
      cancelToken: cancelTokenSource.token,
      params,
    }
    axios
      .get(`/api/v1/predictor`, config)
      .then(({ data }) => {
        if (data?.content) {
          runInAction(() => {
            this.list = data.content
            this.totalPages = data.totalPages
            this.totalElements = data.totalElements
            this.list.forEach((item) => (this.entries[`${item.id}`] = item))
          })
        }
      })
      .catch(() => {
        this.clearList()
      })
      .finally(() => {
        this.setLoadingList(false)
      })

    this.cancelTokenSource = cancelTokenSource.cancel
  }

  searchByName = async (name: string) => {
    return await axios
      .get(`/api/v1/predictor`, {
        params: {
          name,
          size: 10,
        },
      })
      .then(({ data }) => data?.content)
  }

  isNameExists = async (name: string) => {
    return await axios(`/api/v1/predictor/name/exists?name=${name}`).then(
      ({ data }) => data
    )
  }

  getProjectPredictors = async (name: string, otherStatuses?: string) => {
    return await axios.get('/api/v1/predictor/projects', {
      params: {
        name,
        otherStatuses,
        size: 1000,
      },
    }).then(
      ({ data }) => data.content
    )
  }
  getPredictorsByIds = async (ids: number[]) => {
    return await axios.get('/api/v1/predictor', {
      params: {
        ids: ids.join(','),
        size: 1000,
      },
    }).then(
      ({ data }) => data.content as Predictor[]
    )
  }

  refetchListIds = async (ids: number[]) => {
    const exitsIds = ids.filter((id) => this.list.findIndex((item) => item.id === id) !== -1)
    const predictors = await this.getPredictorsByIds(exitsIds)
    predictors.forEach((predictor) => {
      const index =  this.list.findIndex((item) => item.id === predictor.id)
      runInAction(() => {
        this.list[index] = predictor
      })
    });
  }

  getTrainingTypes = async () => {
    return await axios('/api/v1/predictor/training/type').then(
      ({ data }) => data
    )
  }

  getDatasetDependentPredictors = async (trainingDatasetId: number) => {
    return await axios(
      `/api/v1/predictor?size=10000&access_filter=ALL&trainingDatasetId=${trainingDatasetId}`
    ).then(({ data }) => data.content)
  }

  trainPredictor = (id: number) => {
    return axios.put('/api/v1/predictor/' + id + '/train').then(({ data }) => {
      toast.success(
        'Training is started. You will be notified when the process is completed'
      )
      this.setPredictor(data)
    })
  }
  cancelTraining = (id: number) => {
    return axios.put('/api/v1/predictor/' + id + '/cancel').then(({ data }) => {
      toast.success('Started canceling')
      this.setPredictor(data)
    })
  }
  setPredictor = (predictor: Predictor) => {
    const index = this.list.findIndex(
      (item: Predictor) => item.id === predictor.id
    )
    if (index !== -1) {
      this.list[index] = predictor
    }
    this.entries[`${predictor.id}`] = predictor
  }

  makePredictorPrivate = (id: number, shareDetails: boolean) => {
    return axios.put('/api/v1/predictor/' + id + '/private/', {shareDetails}).then(({ data }) => {
      toast.success('The predictor is private now')
      this.setPredictor(data)
    })
  }

  makePredictorPublic = (id: number, shareDetails: boolean) => {
    return axios.put('/api/v1/predictor/' + id + '/public/', {shareDetails}).then(({ data }) => {
      toast.success('The predictor is public now')

      this.setPredictor(data)
    })
  }

  getPredictorDataById = async (id: number) => {
    return await axios('/api/v1/predictor/' + id + '/view').then(
      ({ data }) => data
    )
  }
  getLearningInfo = async (predictorId: number) => {
    return await axios(`/api/v1/predictor/${predictorId}/learning-info`).then(
      ({ data }) => data
    )
  }
  getDELLearningInfo = async (predictorId: number) => {
    return await axios(`/api/v1/predictor/${predictorId}/del-learning-info`).then(
      ({ data }) => data
    )
  }
  getTestingInfo = async (predictorId: number) => {
    return await axios(`/api/v1/predictor/${predictorId}/testing-info`).then(
      ({ data }) => data
    )
  }
  getTrainingInfo = async (predictorId: number) => {
    return await axios(`/api/v1/predictor/${predictorId}/training-info`).then(
      ({ data }) => data
    )
  }
  getLabelPossibleValues = async (predictorId: number, label: string) => {
    return await axios(
      `/api/v1/predictor/${predictorId}/${label}/possible-values`
    ).then(({ data }) => data)
  }

  getPredictorTestFile = async (id: number) => {
    return await axios('/api/v1/predictor/' + id + '/test-file').then(
      ({ data }) => data
    )
  }

  getDocuments = async (id: number) => {
    return await axios('/api/v1/predictor/' + id + '/documents').then(
      ({ data }) => data
    )
  }

  updateDocuments = async (
    id: number,
    body: { deletedFiles: []; newFiles: TemporaryFile[] }
  ) => {
    return await axios
      .put('/api/v1/predictor/' + id + '/documents', body)
      .then(({ data }) => data)
  }

  getUploadUrl: (type: string) => Promise<string> = async (type) => {
    return await axios('/api/v1/predictor/file/upload/url?type=' + type).then(
      ({ data }) => data
    )
  }

  uploadFile = async (fileRef: any) => {
    const onProgress = (event: Progress) => {
      // @ts-ignore
      fileRef.progress = Math.round((100 * event.loaded) / event.total)
      // Re-render the file list to reflect the new progress
      fileRef.updateView()
    }

    try {
      fileRef.url = await FileUploader.uploadFile(
        fileRef.file,
        this.getUploadUrl,
        onProgress
      )
      fileRef.uploaded = true
      fileRef.uploading = false
      // Re-render the file list to reflect the new changes (uploaded = true)
      fileRef.updateView()
      return fileRef.url
    } catch (error) {
      toast.error('Failed to upload file. Please try again.')
      fileRef.progress = 0
      fileRef.error = true
      throw error
    }
  }

  downloadTestData = async (id: number, sort?: object, filters?: any) => {
    const body = {
      sort,
      filters,
    }
    // @ts-ignore
    await axios
      .post('/api/v1/predictor/' + id + '/test/data/csv', body)
      .then(({ data, headers }) => {
        const disposition = headers['content-disposition']
        const matches = /"([^"]*)"/.exec(disposition)
        const filename =
          matches != null && matches[1] ? matches[1] : 'test-data.csv'

        downloadCSV(data, filename)
      })
  }

  downloadAllDocumentsInZipFile = async (id: number) => {
    return await axios('/api/v1/predictor/' + id + '/documents/zip')
    .then(({ data, headers }) => {
      const link = document.createElement('a')
      link.href = data;
      link.download = 'documents.zip'
      link.click()
    })
  }

  getEstimatedCost = async (datasetId: number, numberOfPredictedLabels: number) => {
    return await axios('/api/v1/predictor/cost?datasetId=' + datasetId + '&numberOfPredictedLabels='+numberOfPredictedLabels).then(
      ({ data }) => data
    )
  }

  // Returns the cost of the completed tasks in the operation being executed on the object
  getCostOfCompletedTasks = async (id: number) => {
    return await axios(`/api/v1/predictor/${id}/current-operation-cost`).then(
      ({ data }) => data
    )
  }
}

export const predictorsStore = new PredictorsStore()
